const PageLink = {
  home: '/',
  useCases: '/use-cases',
  login: '/auth/login',
  deviceAuth: '/auth/device',
  // Documentation pages
  docsHub: '/docs',
  docsAtlas: '/docs/atlas',
  docsAtlasGettingStarted: '/docs/atlas/getting-started',
  docsAtlasApprovals: '/docs/atlas/review-and-apply',
  docsAtlasKnowledge: '/docs/atlas/knowledge-and-quality',
  docsAtlasPrivacy: '/docs/atlas/roles-privacy-and-keys',
  docsAtlasTroubleshooting: '/docs/atlas/troubleshooting',
  docsGettingStarted: '/docs/getting-started',
  docsKnowledgeUpload: '/docs/knowledge-upload',
  docsBuildRagSystem: '/docs/build-rag-system',
  docsFaqMigration: '/docs/faq-migration',
  docsPrivateAppChatbotMcp: '/docs/private-app-chatbot-mcp',
  docsMultiBotOrchestration: '/docs/multi-bot-orchestration',
  docsWebsiteIntegration: '/docs/website-integration',
  docsEnterpriseSso: '/docs/enterprise-sso',
  docsChannelIntegrations: '/docs/channel-integrations',
  docsVoiceLines: '/docs/voice-lines',
  docsDocumentStudio: '/docs/document-studio',
  docsHumanHandoff: '/docs/human-handoff',
  docsLeadCapture: '/docs/lead-capture',
  docsAnalyticsRoi: '/docs/analytics-roi',
  docsTeamRoles: '/docs/team-roles',
  docsTuneBot: '/docs/tune-bot',
  docsTroubleshooting: '/docs/troubleshooting',
  docsCitationQuality: '/docs/citation-quality',
  docsSecurityCompliance: '/docs/security-compliance',
  docsAiMyths: '/docs/ai-myths',
  docsApiVsMcp: '/docs/api-vs-mcp',
  docsMcpAgentSetup: '/docs/mcp-agent-setup',
  // External-expert connector guides (one per AI host)
  docsExternalExperts: '/docs/external-experts',
  docsExternalExpertsClaude: '/docs/external-experts/claude',
  docsExternalExpertsChatgpt: '/docs/external-experts/chatgpt',
  docsExternalExpertsCursor: '/docs/external-experts/cursor',
  docsExternalExpertsCodex: '/docs/external-experts/codex',
  docsExternalExpertsGrok: '/docs/external-experts/grok',
  docsExternalExpertsGrokBuild: '/docs/external-experts/grok-build',
  docsApiReference: '/api-docs',
  emailVerification: '/auth/email-verification',
  dashboard: '/dashboard',
  /**
   * The classic dashboard, explicitly. A bare `/dashboard` is the landing decision and sends an Atlas-eligible
   * account to `/home`; Classic's own links back to its dashboard use this so Classic navigation stays in Classic.
   */
  classicDashboard: '/dashboard?view=classic',
  aiBuilder: '/dashboard/ai-builder',
  profile: '/profile',
  notifications: '/notifications',
  billing: '/settings?tab=billing',
  bot: (id: string) => `/bot/${id}/dashboard`,
  botAiBuilder: (id: string) => `/bot/${id}/ai-builder`,
  conversation: (id: string) => `/bot/${id}/conversation`,
  /** The Atlas workspace. */
  atlasHome: '/home',
  /**
   * A new chat with this expert inside the Atlas workspace (WP-LEAD-DQ): /home shows the chat in its main pane,
   * starts a new chat for `new=1` and drops the param.
   */
  expertChat: (id: string) => `/home?expert=${id}&new=1`,
  staff: '/staff',
  // Invoice Intake — internal, its own workspace rather than a tab on a bot.
  intake: '/intake',
  intakeAddresses: '/intake/addresses',
  intakeReceived: '/intake/received',
  intakeDomains: '/intake/domains',
  success: '/payment/success',
  failure: '/payment/failure',
  chatlogs: (id: string) => `/bot/${id}/chatlogs`,
  chatlogsDetails: (id: string, chatlogId: string) => `/bot/${id}/chatlogs/${chatlogId}`,
  leads: (id: string) => `/bot/${id}/leads`,
  handoff: (id: string) => `/bot/${id}/handoff`,
  generatedDocuments: (id: string) => `/bot/${id}/documents`,
  /** One generated document open in Document Studio. */
  botDocument: (id: string, documentId: string) =>
    `/bot/${id}/documents?documentId=${encodeURIComponent(documentId)}`,
  source: (id: string) => `/bot/${id}/links`,
  docs: (id: string) => `/bot/${id}/docs`,
  links: (id: string) => `/bot/${id}/links`,
  schedules: (id: string) => `/bot/${id}/schedules`,
  text: (id: string) => `/bot/${id}/text`,
  QA: (id: string) => `/bot/${id}/qa`,
  audio: (id: string) => `/bot/${id}/audio`,
  mcpTools: (id: string) => `/bot/${id}/mcp-tools`,
  audioReview: (id: string) => `/bot/${id}/source/audio-review`,
  tuneBot: (id: string) => `/bot/${id}/tune-bot`,
  deployment: (id: string) => `/bot/${id}/deployment`,
  integrations: (id: string) => `/bot/${id}/integrations`,
  roi: (id: string) => `/bot/${id}/roi`,
  knowledgeGaps: (id: string) => `/bot/${id}/knowledge-gaps`,
  crawlLinks: (id: string) => `/bot/${id}/source/crawls`,
  addSource: (id: string) => `/bot/${id}/source/add`,
  addDocs: (id: string) => `/bot/${id}/docs/add`,
  addLinks: (id: string) => `/bot/${id}/links/add`,
  viewCrawls: (id: string) => `/bot/${id}/links/crawls`,
  addText: (id: string) => `/bot/${id}/text`,
  qa: (id: string) => `/bot/${id}/qa`,
  settings: (id: string) => `/bot/${id}/settings`,
  voice: (id: string) => `/bot/${id}/voice`,
  voiceAppointments: (id: string) => `/bot/${id}/voice-appointments`,
  aboutUs: '/about-us',
  press: '/press',
  contactUs: '/contact-us',
  schools: '/schools',
  companyBrain: '/company-brain',
  demo: '/demo',
  pricing: '/#pricing-brand',
  marketplace: '/marketplace',
  marketplaceImports: '/marketplace/imports',
  marketplaceListing: (slug: string) => `/marketplace/${slug}`,
  marketplaceCreators: '/marketplace/creators',
  marketplaceCreator: (slug: string) => `/marketplace/creators/${slug}`,
  marketplaceBecomeCreator: '/marketplace/become-a-creator',
  creator: '/creator',
  creatorGovernance: '/creator/governance',
  creatorListing: (id: string) => `/creator/listings/${id}`,
  creatorListingPricing: (id: string) => `/creator/listings/${id}/pricing`,
  creatorListingDashboard: (id: string) => `/creator/listings/${id}/dashboard`,
  creatorListingConversation: (id: string) => `/creator/listings/${id}/conversation`,
  creatorListingChatlogs: (id: string) => `/creator/listings/${id}/chatlogs`,
  creatorListingHandoff: (id: string) => `/creator/listings/${id}/handoff`,
  creatorListingKnowledge: (id: string) => `/creator/listings/${id}/knowledge`,
  creatorListingDocs: (id: string) => `/creator/listings/${id}/docs`,
  creatorListingLinks: (id: string) => `/creator/listings/${id}/links`,
  creatorListingSchedules: (id: string) => `/creator/listings/${id}/schedules`,
  creatorListingText: (id: string) => `/creator/listings/${id}/text`,
  creatorListingQa: (id: string) => `/creator/listings/${id}/qa`,
  creatorListingAudio: (id: string) => `/creator/listings/${id}/audio`,
  creatorListingMcpTools: (id: string) => `/creator/listings/${id}/mcp-tools`,
  creatorListingTuneBot: (id: string) => `/creator/listings/${id}/tune-bot`,
  creatorListingAppearance: (id: string) => `/creator/listings/${id}/appearance`,
  creatorListingDeployment: (id: string) => `/creator/listings/${id}/deployment`,
  creatorListingIntegrations: (id: string) => `/creator/listings/${id}/integrations`,
  creatorListingRoi: (id: string) => `/creator/listings/${id}/roi`,
  creatorListingKnowledgeGaps: (id: string) => `/creator/listings/${id}/knowledge-gaps`,
  creatorListingSettings: (id: string) => `/creator/listings/${id}/settings`,
  webinars: '/webinars',
  faqs: '/faqs',
  terms: '/terms-condition',
  privacy: '/privacy-policy',
  privacyPage: '/privacy', // marketing one-pager, distinct from the legal privacy policy
  support: '/support',
  analytics: '/analytics',
};

export default PageLink;
